import AssociatedDeviceModal from '@/components/AssociatedDeviceModal';
import type { AssociatedDevice } from '@/components/AssociatedDeviceModal';
import { Button, Form, message, PopConfirm, Select, SubContent, Table, Tag } from '@maxtropy/components';
import {
  apiV2MicrogridTopologyTopologyDetailPost,
  apiV2MicrogridTopologyTopologySavePost,
  apiV2MicrogridTopologyUetOptionListPost,
} from '@maxtropy/dmes-apis-v2';
import { apiV2DevicePagePost as apiV2IdsDevicePagePost } from '@maxtropy/intelligent-dispath-apis-v2';
import { useRequest } from 'ahooks';
import { Col, Row, Space } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/hooks';
import BaselineDataModal from './BaselineDataModal';
import EditAuxiliaryDeviceModal from './EditAuxiliaryDeviceModal';
import EditLoadDeviceModal from './EditLoadDeviceModal';
import styles from './index.module.scss';
import { BaselineDataRecord, TopologyAuxiliaryRecord, TopologyLoadDeviceRecord } from './types';

interface TopologyFormValues {
  totalLoad?: number[];
  powerPlant?: number;
  pvStation?: number;
  storageStation?: number;
}

export interface MicrogridTopologyTabRef {
  save: () => Promise<void>;
  isEditing: boolean;
  loading: boolean;
}

const loadDeviceTypeText: Record<TopologyLoadDeviceRecord['type'], string> = {
  FERROSILICON_FURNACE: '硅铁炉',
  CALCIUM_CARBIDE_FURNACE: '电石炉',
};

const SMART_METER_DEVICE_TYPE_ID = 5002;

type RelatedDeviceModalTarget =
  | {
      type: 'load';
      id: number;
    }
  | {
      type: 'auxiliary';
      id: number;
    };

const renderRelatedDevices = (value?: string[]) => (value?.length ? value.join('、') : '-');

const getAssociatedDeviceId = (device: AssociatedDevice) => device.id ?? device.deviceId;

const getAssociatedDeviceName = (device: AssociatedDevice) =>
  device.name ?? device.deviceName ?? device.code ?? device.deviceCode;

const MicrogridTopologyTab = forwardRef<MicrogridTopologyTabRef>((_, ref) => {
  const hasModelingPermission = useHasPermission(PermissionsType.B_MICROGRID_TOPOLOGY_MODELING);
  const [form] = Form.useForm<TopologyFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [gridMeterNameMap, setGridMeterNameMap] = useState<Record<string, string>>({});
  const { data: uetOptions, loading: uetOptionsLoading } = useRequest(() =>
    apiV2MicrogridTopologyUetOptionListPost({}).then(res => {
      const totalLoad = (res.loadList ?? []).map(item => ({ label: item.name!, value: item.id }));
      const powerPlant = (res.thermalList ?? []).map(item => ({ label: item.name!, value: item.id }));
      const pvStation = (res.pvList ?? []).map(item => ({ label: item.name!, value: item.id }));
      const storageStation = (res.storageList ?? []).map(item => ({ label: item.name!, value: item.id }));

      return {
        totalLoad: totalLoad ?? [],
        powerPlant: powerPlant ?? [],
        pvStation: pvStation ?? [],
        storageStation: storageStation ?? [],
      };
    })
  );
  const [loadDevices, setLoadDevices] = useState<TopologyLoadDeviceRecord[]>([]);
  const [auxiliaryDevices, setAuxiliaryDevices] = useState<TopologyAuxiliaryRecord[]>([]);
  const [gridMeters, setGridMeters] = useState<number[]>([]);
  const [baselineDataList, setBaselineDataList] = useState<BaselineDataRecord[]>([]);
  const [loadDeviceModalOpen, setLoadDeviceModalOpen] = useState(false);
  const [editingLoadDevice, setEditingLoadDevice] = useState<TopologyLoadDeviceRecord>();
  const [auxiliaryDeviceModalOpen, setAuxiliaryDeviceModalOpen] = useState(false);
  const [editingAuxiliaryDevice, setEditingAuxiliaryDevice] = useState<TopologyAuxiliaryRecord>();
  const [baselineDataModalOpen, setBaselineDataModalOpen] = useState(false);
  const [gridMeterModalOpen, setGridMeterModalOpen] = useState(false);
  const [relatedDeviceModalTarget, setRelatedDeviceModalTarget] = useState<RelatedDeviceModalTarget>();
  const { runAsync: saveTopology, loading: saving } = useRequest(apiV2MicrogridTopologyTopologySavePost, {
    manual: true,
  });
  const { refreshAsync: refreshTopologyDetail, loading: detailLoading } = useRequest(
    () =>
      apiV2MicrogridTopologyTopologyDetailPost({}).then(res => {
        form.setFieldsValue({
          totalLoad: (res.loadUet ?? []).map(i => i.id!),
          powerPlant: res.thermalStation?.id,
          pvStation: res.pvStation?.id,
          storageStation: res.storageStation?.id,
        });

        setGridMeters((res.gridDevice ?? []).map(item => item.id!));
        setGridMeterNameMap(
          res.gridDevice?.reduce<Record<number, string>>((map, item) => {
            if (item.id !== undefined && item.name) {
              map[item.id] = item.name;
            }

            return map;
          }, {}) ?? {}
        );
        setLoadDevices(
          (res.loadObjectList ?? [])
            .filter(item => item.type === 'FURNACE')
            .map((item, index) => ({
              id: item.id ?? Date.now() + index,
              backendId: item.id,
              name: item.name ?? '',
              type: item.extra?.deviceType ?? 'FERROSILICON_FURNACE',
              ratedVoltage: item.extra?.ratedVoltage ?? 0,
              relatedDevices: (item.deviceList ?? []).map(device => device.name!),
              relatedDeviceIds: (item.deviceList ?? []).map(device => device.id!),
            }))
        );
        setAuxiliaryDevices(
          (res.loadObjectList ?? [])
            .filter(item => item.type === 'AUXILIARY')
            .map((item, index) => ({
              id: item.id ?? Date.now() + index,
              backendId: item.id,
              name: item.name ?? '',
              relatedDevices: (item.deviceList ?? [])?.map(device => device.name!),
              relatedDeviceIds: (item.deviceList ?? []).map(device => device.id!),
            }))
        );
        setBaselineDataList(res.baselineDataList ?? []);

        return res;
      }),
    {}
  );

  const loadColumns: ColumnsType<TopologyLoadDeviceRecord> = useMemo(
    () => [
      {
        title: '负荷设备名称',
        dataIndex: 'name',
        width: 120,
      },
      {
        title: '设备类型',
        dataIndex: 'type',
        width: 120,
        render: value => loadDeviceTypeText[value as TopologyLoadDeviceRecord['type']] ?? value,
      },
      {
        title: '额定电压(V)',
        dataIndex: 'ratedVoltage',
        width: 120,
      },
      {
        title: '关联设备',
        dataIndex: 'relatedDevices',
        render: renderRelatedDevices,
      },
      {
        title: '操作',
        width: 180,
        render: (_, record) => (
          <Space size={12}>
            <Button
              type="link"
              htmlType="button"
              disabled={!isEditing}
              onClick={() => {
                setEditingLoadDevice(record);
                setLoadDeviceModalOpen(true);
              }}
            >
              编辑
            </Button>
            <Button
              type="link"
              htmlType="button"
              disabled={!isEditing}
              onClick={() => setRelatedDeviceModalTarget({ type: 'load', id: record.id })}
            >
              关联设备
            </Button>
            <PopConfirm
              title="确认删除该负荷设备吗？"
              okText="确定"
              cancelText="取消"
              disabled={!isEditing}
              onConfirm={() => setLoadDevices(list => list.filter(item => item.id !== record.id))}
            >
              <Button type="link" htmlType="button" danger disabled={!isEditing}>
                删除
              </Button>
            </PopConfirm>
          </Space>
        ),
      },
    ],
    [isEditing]
  );

  const auxiliaryColumns: ColumnsType<TopologyAuxiliaryRecord> = useMemo(
    () => [
      {
        title: '辅助用能名称',
        dataIndex: 'name',
        width: 160,
      },
      {
        title: '关联设备',
        dataIndex: 'relatedDevices',
        render: renderRelatedDevices,
      },
      {
        title: '操作',
        width: 180,
        render: (_, record) => (
          <Space size={12}>
            <Button
              type="link"
              htmlType="button"
              disabled={!isEditing}
              onClick={() => {
                setEditingAuxiliaryDevice(record);
                setAuxiliaryDeviceModalOpen(true);
              }}
            >
              编辑
            </Button>
            <Button
              type="link"
              htmlType="button"
              disabled={!isEditing}
              onClick={() => setRelatedDeviceModalTarget({ type: 'auxiliary', id: record.id })}
            >
              关联设备
            </Button>
            <PopConfirm
              title="确认删除该辅助用能吗？"
              okText="确定"
              cancelText="取消"
              disabled={!isEditing}
              onConfirm={() => setAuxiliaryDevices(list => list.filter(item => item.id !== record.id))}
            >
              <Button type="link" htmlType="button" danger disabled={!isEditing}>
                删除
              </Button>
            </PopConfirm>
          </Space>
        ),
      },
    ],
    [isEditing]
  );

  const addLoadDevice = () => {
    setEditingLoadDevice(undefined);
    setLoadDeviceModalOpen(true);
  };

  const handleLoadDeviceModalOk = (values: TopologyLoadDeviceRecord) => {
    setLoadDevices(list => {
      const existingRecord = list.some(item => item.id === values.id);

      if (existingRecord) {
        return list.map(item => (item.id === values.id ? values : item));
      }

      return [...list, values];
    });
    setLoadDeviceModalOpen(false);
    setEditingLoadDevice(undefined);
  };

  const addAuxiliaryDevice = () => {
    setEditingAuxiliaryDevice(undefined);
    setAuxiliaryDeviceModalOpen(true);
  };

  const handleAuxiliaryDeviceModalOk = (values: TopologyAuxiliaryRecord) => {
    setAuxiliaryDevices(list => {
      const existingRecord = list.some(item => item.id === values.id);

      if (existingRecord) {
        return list.map(item => (item.id === values.id ? values : item));
      }

      return [...list, values];
    });
    setAuxiliaryDeviceModalOpen(false);
    setEditingAuxiliaryDevice(undefined);
  };

  const selectedGridMeterDevices = useMemo<AssociatedDevice[]>(
    () =>
      gridMeters.map(item => ({
        id: item,
        name: gridMeterNameMap[item] ?? String(item),
      })),
    [gridMeterNameMap, gridMeters]
  );

  const handleGridMeterModalOk = (devices: AssociatedDevice[]) => {
    setGridMeters(devices.map(item => getAssociatedDeviceId(item)).filter((id): id is number => id !== undefined));
    setGridMeterNameMap(map => ({
      ...map,
      ...devices.reduce<Record<string, string>>((nextMap, item) => {
        const deviceId = getAssociatedDeviceId(item);
        const deviceName = getAssociatedDeviceName(item);

        if (deviceId !== undefined && deviceName) {
          nextMap[deviceId] = deviceName;
        }

        return nextMap;
      }, {}),
    }));
    setGridMeterModalOpen(false);
  };

  const selectedRelatedDevices = useMemo<AssociatedDevice[]>(() => {
    if (!relatedDeviceModalTarget) {
      return [];
    }

    const record =
      relatedDeviceModalTarget.type === 'load'
        ? loadDevices.find(item => item.id === relatedDeviceModalTarget.id)
        : auxiliaryDevices.find(item => item.id === relatedDeviceModalTarget.id);

    return (
      record?.relatedDeviceIds?.map((id, index) => ({
        id,
        name: record.relatedDevices[index] ?? String(id),
      })) ?? []
    );
  }, [auxiliaryDevices, loadDevices, relatedDeviceModalTarget]);

  const handleRelatedDeviceModalOk = (devices: AssociatedDevice[]) => {
    if (!relatedDeviceModalTarget) {
      return;
    }

    const nextDevices = relatedDeviceModalTarget.type === 'load' ? devices.slice(0, 1) : devices;
    const relatedDeviceIds = nextDevices
      .map(item => getAssociatedDeviceId(item))
      .filter((id): id is number => id !== undefined);
    const relatedDevices = nextDevices
      .map(item => getAssociatedDeviceName(item))
      .filter((name): name is string => Boolean(name));

    if (relatedDeviceModalTarget.type === 'load') {
      setLoadDevices(list =>
        list.map(item =>
          item.id === relatedDeviceModalTarget.id
            ? {
                ...item,
                relatedDeviceIds,
                relatedDevices,
              }
            : item
        )
      );
    } else {
      setAuxiliaryDevices(list =>
        list.map(item =>
          item.id === relatedDeviceModalTarget.id
            ? {
                ...item,
                relatedDeviceIds,
                relatedDevices,
              }
            : item
        )
      );
    }

    setRelatedDeviceModalTarget(undefined);
  };

  const onFinish = async (values: TopologyFormValues) => {
    await saveTopology({
      loadUetIdList: values.totalLoad,
      thermalStationId: values.powerPlant,
      pvStationId: values.pvStation,
      storageStationId: values.storageStation,
      gridDeviceIdList: gridMeters,
      loadObjectList: [
        ...loadDevices.map(item => ({
          id: item.backendId,
          type: 'FURNACE' as const,
          name: item.name,
          extra: {
            ratedVoltage: item.ratedVoltage,
            deviceType: item.type,
          },
          deviceIdList: item.relatedDeviceIds ?? [],
        })),
        ...auxiliaryDevices.map(item => ({
          id: item.backendId,
          type: 'AUXILIARY' as const,
          name: item.name,
          deviceIdList: item.relatedDeviceIds ?? [],
        })),
      ],
      baselineDataList,
    });
    await refreshTopologyDetail();
    message.success('保存成功');
    setIsEditing(false);
  };

  const save = async () => {
    const values = await form.validateFields();
    await onFinish(values);
  };

  const handleCancel = async () => {
    await refreshTopologyDetail();
    setIsEditing(false);
  };

  useImperativeHandle(ref, () => ({
    save,
    isEditing,
    loading: uetOptionsLoading || detailLoading || saving,
  }));

  return (
    <>
      <Form form={form} className={styles.form} layout="horizontal" disabled={!isEditing} onFinish={onFinish}>
        <SubContent title="微电网对象配置">
          <Row gutter={96}>
            <Col span={12}>
              <Form.Item label="总负荷" name="totalLoad">
                <Select
                  mode="multiple"
                  placeholder="请选择"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={uetOptionsLoading || detailLoading}
                  options={uetOptions?.totalLoad ?? []}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="火电站" name="powerPlant">
                <Select
                  placeholder="请选择"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={uetOptionsLoading || detailLoading}
                  options={uetOptions?.powerPlant ?? []}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={96}>
            <Col span={12}>
              <Form.Item label="光伏站" name="pvStation">
                <Select
                  placeholder="请选择"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={uetOptionsLoading || detailLoading}
                  options={uetOptions?.pvStation ?? []}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="储能站" name="storageStation">
                <Select
                  placeholder="请选择"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={uetOptionsLoading || detailLoading}
                  options={uetOptions?.storageStation ?? []}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={96}>
            <Col span={24}>
              <Form.Item label="市电" className={styles.inlineActionItem}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', rowGap: 8 }}>
                  <Button
                    type="link"
                    htmlType="button"
                    className={styles.linkButton}
                    disabled={!isEditing}
                    onClick={() => setGridMeterModalOpen(true)}
                  >
                    配置
                  </Button>
                  <Space size={4} wrap>
                    {gridMeters.map(item => (
                      <Tag
                        closable={isEditing}
                        key={item}
                        onClose={() => setGridMeters(list => list.filter(gridMeter => gridMeter !== item))}
                      >
                        {gridMeterNameMap[item] ?? item}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </SubContent>

        <SubContent title="负荷侧配置">
          <Button
            type="primary"
            htmlType="button"
            wrapStyle={{ marginBottom: 10 }}
            disabled={!isEditing}
            onClick={addLoadDevice}
          >
            新增负荷设备
          </Button>
          <Table rowKey="id" pagination={false} columns={loadColumns} dataSource={loadDevices} />
          <Button
            type="primary"
            htmlType="button"
            wrapStyle={{ marginBottom: 10 }}
            disabled={!isEditing}
            onClick={addAuxiliaryDevice}
          >
            新增辅助用能
          </Button>
          <Table rowKey="id" pagination={false} columns={auxiliaryColumns} dataSource={auxiliaryDevices} />
        </SubContent>

        <SubContent title="基准数据配置">
          <Form.Item label="导入基准数据" className={styles.inlineActionItem}>
            <Button
              type="link"
              htmlType="button"
              className={styles.linkButton}
              onClick={() => setBaselineDataModalOpen(true)}
            >
              配置
            </Button>
          </Form.Item>
        </SubContent>
      </Form>

      <Space size={8} className="sticky-footer" style={{ zIndex: 99, paddingLeft: 32 }}>
        {isEditing ? (
          <>
            <Button type="primary" htmlType="button" loading={saving} onClick={() => form.submit()}>
              保存
            </Button>
            <Button htmlType="button" onClick={handleCancel}>
              取消
            </Button>
          </>
        ) : (
          hasModelingPermission && (
            <Button type="primary" htmlType="button" onClick={() => setIsEditing(true)}>
              编辑
            </Button>
          )
        )}
      </Space>
      <EditLoadDeviceModal
        open={loadDeviceModalOpen}
        record={editingLoadDevice}
        records={loadDevices}
        onCancel={() => {
          setLoadDeviceModalOpen(false);
          setEditingLoadDevice(undefined);
        }}
        onOk={handleLoadDeviceModalOk}
      />
      <EditAuxiliaryDeviceModal
        open={auxiliaryDeviceModalOpen}
        record={editingAuxiliaryDevice}
        records={auxiliaryDevices}
        onCancel={() => {
          setAuxiliaryDeviceModalOpen(false);
          setEditingAuxiliaryDevice(undefined);
        }}
        onOk={handleAuxiliaryDeviceModalOk}
      />
      <BaselineDataModal
        open={baselineDataModalOpen}
        onCancel={() => setBaselineDataModalOpen(false)}
        onOk={values => {
          setBaselineDataList(values);
          setBaselineDataModalOpen(false);
        }}
      />
      {gridMeterModalOpen && (
        <AssociatedDeviceModal
          title="市电设备选择"
          typeIds={[SMART_METER_DEVICE_TYPE_ID]}
          requestDevicePage={apiV2IdsDevicePagePost}
          selectedDevices={selectedGridMeterDevices}
          onCancel={() => setGridMeterModalOpen(false)}
          onOk={handleGridMeterModalOk}
        />
      )}
      {relatedDeviceModalTarget && (
        <AssociatedDeviceModal
          title="关联设备选择"
          typeIds={[SMART_METER_DEVICE_TYPE_ID]}
          requestDevicePage={apiV2IdsDevicePagePost}
          selectionType={relatedDeviceModalTarget.type === 'load' ? 'radio' : 'checkbox'}
          selectedDevices={selectedRelatedDevices}
          onCancel={() => setRelatedDeviceModalTarget(undefined)}
          onOk={handleRelatedDeviceModalOk}
        />
      )}
    </>
  );
});

export default MicrogridTopologyTab;
